import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState, useCallback, useMemo, startTransition } from 'react';
import * as d3 from 'd3';
import { OverlayPanel } from 'primereact/overlaypanel';
import type { Sphere } from 'types/global';
import {
  OrbitConfig,
  NodeData,
  ORBITS,
  SVG_WIDTH,
  SVG_HEIGHT,
  CENTER_X,
  CENTER_Y,
  SPINNING_PHASE_DURATION,
  SETTLEMENT_STAGGER_MS,
} from './constants';
import type { AnimationPhase } from './constants';
import {
  createOrbitRings,
  createOrbitNodes,
  startOrbitAnimationFromCurrent,
  startOrbitAnimationFromCurrentWithVelocity,
  stopAnimations,
  rotateOrbitToPosition,
  settleAllOrbits,
} from './animations';
import { DialRenderer, DialType } from '../../atoms/Dials';
import StatusPopoverMolecule from '../Atlas/StatusPopover/StatusPopoverMolecule';
import ContextSelectorPopoverMolecule from '../ContextSelectorPopover/ContextSelectorPopoverMolecule';
import { getStatusColor } from '../../../utils/statusColors';
import defaultCenterImage from '../../../assets/images/center.iris-border.png';
import './OrbitalVisualizationMolecule.scss';

export interface SelectedNodeInfo {
  nodeId: string;
  nodeIndex: number;
  orbitIndex: number;
  label: string;
}

export interface OrbitalVisualizationMoleculeProps {
  orbits?: OrbitConfig[];
  nodes?: NodeData[][];
  /** Type of dial to display in the center */
  dialType?: DialType;
  /** Sphere data for the dial */
  sphere?: Sphere | null;
  /** Callback when dial is clicked */
  onDialClick?: () => void;
  isAnimating?: boolean;
  className?: string;
  centerImagePath?: string;
  onNodeClick?: (nodeInfo: SelectedNodeInfo) => void;
  selectedOrbitIndex?: number | null;
  selectedOrbits?: Set<number>;
  containerRef?: React.RefObject<HTMLDivElement>;
  /** Enable velocity-based animation speeds (default: false) */
  useVelocitySpeed?: boolean;
  /** Callback when refresh icon is clicked */
  onRefresh?: () => void;
  /** Callback when conversation icon is clicked */
  onConversationClick?: () => void;
  /** Callback when edit icon is clicked */
  onEditClick?: () => void;
  /** Callback when add icon is clicked */
  onAddClick?: () => void;
  /** Callback when bell (notifications) icon is clicked - triggers mode change */
  onNotificationsClick?: () => void;
  /**
   * Set only when the armillary is inside a metaspace with the toolbar active.
   * When present, clicking the ring's status icon opens the toolbar's status
   * tool (scoped to the centered sphere) instead of the inline StatusPopover.
   * Undefined for standalone armillary, where the popover remains.
   */
  onStatusToolClick?: () => void;
  /** Callback when status factor is deployed (for status dial) */
  onStatusDeployed?: () => void;
  /** Callback to open sphere editor to configure factors (for status dial) */
  onConfigureFactors?: (sphereId: string) => void;
  /** Callback when a node is double-clicked (for navigation) */
  onNodeDoubleClick?: (nodeInfo: SelectedNodeInfo) => void;
  /** Callback when the dial type changes (via arrow cycling) */
  onDialChange?: (dialType: DialType) => void;
  /** Callback when a context is selected from the context popover */
  onContextSelect?: (systemName: string, displayName: string) => void;
  /** The currently active context system name */
  activeContextSystemName?: string;
  /** Callback when filter icon is clicked (delegates to external context switcher) */
  onContextFilterClick?: (event: MouseEvent) => void;
  /** Callback when animations complete settlement (nodes stop moving) */
  onAnimationSettled?: () => void;
}

export interface NodePosition {
  x: number;
  y: number;
}

export interface OrbitalVisualizationMoleculeRef {
  rotateToNode: (orbitIndex: number, nodeIndex: number, targetAngle?: number) => Promise<NodePosition | null>;
}

const OrbitalVisualizationMolecule = forwardRef<OrbitalVisualizationMoleculeRef, OrbitalVisualizationMoleculeProps>(({
  orbits = ORBITS,
  nodes = [],
  dialType = 'avatar',
  sphere = null,
  onDialClick,
  isAnimating = true,
  className = '',
  centerImagePath = defaultCenterImage,
  onNodeClick,
  selectedOrbitIndex = null,
  selectedOrbits = new Set(),
  containerRef,
  useVelocitySpeed = false,
  onRefresh,
  onConversationClick,
  onEditClick,
  onAddClick,
  onNotificationsClick,
  onStatusToolClick,
  onStatusDeployed,
  onConfigureFactors,
  onNodeDoubleClick,
  onDialChange,
  onContextSelect,
  activeContextSystemName = 'center_armillary_context',
  onContextFilterClick,
  onAnimationSettled,
}, ref) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerDivRef = useRef<HTMLDivElement>(null);
  const isInitialized = useRef(false);
  const lastNodesJson = useRef<string>('');
  const lastOrbitsJson = useRef<string>('');
  const [dialSize, setDialSize] = useState<number>(0);

  // Settlement animation lifecycle refs
  const settlementTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animationPhaseRef = useRef<AnimationPhase>('spinning');

  // Refs for settlement countdown — keeps callback identity stable so Effect 2 doesn't re-fire
  const statusBasedOrbitsRef = useRef<OrbitConfig[]>([]);
  const nodesRef = useRef<NodeData[][]>([]);
  const selectedOrbitsRef = useRef<Set<number>>(new Set());

  // Available dial types for cycling
  const DIAL_TYPES: DialType[] = ['avatar', 'status', 'tasks'];

  // Initialize dial index based on dialType prop
  const initialDialIndex = DIAL_TYPES.indexOf(dialType);
  const [currentDialIndex, setCurrentDialIndex] = useState<number>(Math.max(0, initialDialIndex));

  // Ref for status popover
  const statusPopoverRef = useRef<OverlayPanel>(null);
  const statusIconRef = useRef<SVGGElement | null>(null);

  // Ref for context selector popover
  const contextPopoverRef = useRef<OverlayPanel>(null);
  const contextIconRef = useRef<SVGGElement | null>(null);
  // Hidden HTML anchor div — PrimeReact's OverlayPanel needs a real HTML element
  // for getBoundingClientRect() to work correctly (SVG <g> elements are unreliable)
  const contextAnchorRef = useRef<HTMLDivElement>(null);

  // Use refs for callbacks to avoid stale closures in D3 event handlers
  const onRefreshRef = useRef(onRefresh);
  const onNodeClickRef = useRef(onNodeClick);
  const onConversationClickRef = useRef(onConversationClick);
  const onEditClickRef = useRef(onEditClick);
  const onAddClickRef = useRef(onAddClick);
  const onNotificationsClickRef = useRef(onNotificationsClick);
  const onNodeDoubleClickRef = useRef(onNodeDoubleClick);
  const onDialChangeRef = useRef(onDialChange);
  const onContextFilterClickRef = useRef(onContextFilterClick);
  const onAnimationSettledRef = useRef(onAnimationSettled);

  // Ref for status icon click handler (to open popover)
  const onStatusIconClickRef = useRef<(event: MouseEvent) => void>(() => { /* no-op until initialized */ });

  // Ref for the toolbar status-tool callback (present only inside a metaspace
  // toolbar). Kept in a ref so the once-created D3 click handler always reads
  // the latest organism callback — which itself closes over the live centered
  // sphere id, so there's no stale-sphere risk despite the D3 handler's fixed
  // closure.
  const onStatusToolClickRef = useRef(onStatusToolClick);

  // Ref for context icon click handler (to open context popover)
  const onContextIconClickRef = useRef<(event: MouseEvent) => void>(() => { /* no-op until initialized */ });

  // Refs for dial navigation (used by D3 arrow click handlers)
  const goToPrevDialRef = useRef(() => {
    // Use startTransition for non-urgent dial index updates
    // Prevents blocking animations with React re-renders
    startTransition(() => {
      setCurrentDialIndex(prev => (prev - 1 + DIAL_TYPES.length) % DIAL_TYPES.length);
    });
  });
  const goToNextDialRef = useRef(() => {
    // Use startTransition for non-urgent dial index updates
    // Prevents blocking animations with React re-renders
    startTransition(() => {
      setCurrentDialIndex(prev => (prev + 1) % DIAL_TYPES.length);
    });
  });

  // Keep refs up to date
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    onStatusToolClickRef.current = onStatusToolClick;
  }, [onStatusToolClick]);

  useEffect(() => {
    onNodeClickRef.current = onNodeClick;
  }, [onNodeClick]);

  useEffect(() => {
    onConversationClickRef.current = onConversationClick;
  }, [onConversationClick]);

  useEffect(() => {
    onEditClickRef.current = onEditClick;
  }, [onEditClick]);

  useEffect(() => {
    onAddClickRef.current = onAddClick;
  }, [onAddClick]);

  useEffect(() => {
    onNotificationsClickRef.current = onNotificationsClick;
  }, [onNotificationsClick]);

  useEffect(() => {
    onNodeDoubleClickRef.current = onNodeDoubleClick;
  }, [onNodeDoubleClick]);

  useEffect(() => {
    onDialChangeRef.current = onDialChange;
  }, [onDialChange]);

  useEffect(() => {
    onAnimationSettledRef.current = onAnimationSettled;
  }, [onAnimationSettled]);

  // Notify parent when dial type changes via arrow cycling
  useEffect(() => {
    if (onDialChangeRef.current) {
      onDialChangeRef.current(DIAL_TYPES[currentDialIndex]);
    }
  }, [currentDialIndex]);

  // Setup status icon click handler to open popover
  useEffect(() => {
    onStatusIconClickRef.current = (event: MouseEvent) => {
      if (statusPopoverRef.current && statusIconRef.current) {
        // Create a synthetic-like event object with the status icon as target
        // This ensures the popover positions next to the icon, not at the event coordinates
        const syntheticEvent = {
          currentTarget: statusIconRef.current,
          target: statusIconRef.current,
        } as unknown as React.SyntheticEvent;
        statusPopoverRef.current.toggle(syntheticEvent);
      }
    };
  }, []);

  // Setup context icon click handler to open context popover
  useEffect(() => {
    onContextIconClickRef.current = (event: MouseEvent) => {
      // If an external context filter handler is provided, delegate to it (Armillary use case)
      if (onContextFilterClickRef.current) {
        onContextFilterClickRef.current(event);
        return;
      }

      // Otherwise use the built-in ContextSelectorPopoverMolecule
      if (!contextPopoverRef.current) return;
      event.stopPropagation();

      const clickX = event.clientX;
      const clickY = event.clientY;
      const popupWidth = 280;
      const gap = 8;

      // Toggle to show/hide the panel (PrimeReact will position it incorrectly — we fix it below)
      contextPopoverRef.current.toggle(event as unknown as React.SyntheticEvent);

      // After PrimeReact renders the panel, override its position using the actual click coordinates.
      // We use requestAnimationFrame twice: first frame React commits the DOM,
      // second frame we can safely override the position before the browser paints.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const panelEl = document.querySelector('.context-selector-popover') as HTMLElement | null;
          if (!panelEl || panelEl.style.display === 'none') return;
          const left = (window.innerWidth - clickX) >= popupWidth + gap
            ? clickX
            : Math.max(gap, clickX - popupWidth);
          panelEl.style.position = 'fixed';
          panelEl.style.top = `${clickY + gap}px`;
          panelEl.style.left = `${left}px`;
        });
      });
    };
  }, []);

  // Settlement lifecycle: clear any pending settlement timer
  const clearSettlementTimer = useCallback(() => {
    if (settlementTimerRef.current !== null) {
      clearTimeout(settlementTimerRef.current);
      settlementTimerRef.current = null;
    }
  }, []);

  // Helper to create solid status color for orbit ring
  const createStatusSolidColor = useCallback((statusColor: string): string[] => {
    // Return single solid color for the ring
    return [statusColor];
  }, []);

  // Create orbits with status-based colors for the inner ring (orbit index 2)
  const currentStatus = sphere?.status?.currentValue?.status || 'unknown';
  const statusColor = getStatusColor(currentStatus);

  const statusBasedOrbits = useMemo((): OrbitConfig[] => {
    return orbits.map((orbit, index) => {
      if (index === 2) {
        // Replace orbit 2's colors with solid status color
        return {
          ...orbit,
          colors: createStatusSolidColor(statusColor)
        };
      }
      return orbit;
    });
  }, [orbits, statusColor, createStatusSolidColor]);

  // Keep settlement refs in sync with latest values
  statusBasedOrbitsRef.current = statusBasedOrbits;
  nodesRef.current = nodes;
  selectedOrbitsRef.current = selectedOrbits;

  // Settlement lifecycle: start countdown that triggers orbit settlement after spinning phase
  // Reads from refs so the callback identity stays stable (no deps that change every render)
  const startSettlementCountdown = useCallback(() => {
    clearSettlementTimer();
    animationPhaseRef.current = 'spinning';

    settlementTimerRef.current = setTimeout(() => {
      if (!svgRef.current) return;
      animationPhaseRef.current = 'decelerating';
      const svg = d3.select(svgRef.current);

      settleAllOrbits(svg, statusBasedOrbitsRef.current, nodesRef.current, SETTLEMENT_STAGGER_MS, selectedOrbitsRef.current)
        .then(() => {
          animationPhaseRef.current = 'settled';
          // Notify parent that automatic settlement has completed
          if (onAnimationSettledRef.current) {
            onAnimationSettledRef.current();
          }
        });
    }, SPINNING_PHASE_DURATION);
  }, [clearSettlementTimer]);

  // Calculate dial size based on actual rendered SVG dimensions
  useEffect(() => {
    const calculateDialSize = () => {
      if (!containerDivRef.current) return;

      const container = containerDivRef.current;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      // Account for padding (8px on each side from SCSS)
      const padding = 8;
      const availableWidth = containerWidth - padding * 2;
      const availableHeight = containerHeight - padding * 2;

      // SVG uses preserveAspectRatio="xMidYMid meet", so it scales to fit the smaller dimension
      const scale = Math.min(availableWidth / SVG_WIDTH, availableHeight / SVG_HEIGHT);

      // Dial radius should fit inside the white inner ring
      const dialViewBoxRadius = 82;
      const dialViewBoxDiameter = dialViewBoxRadius * 2;

      // Calculate actual pixel size
      const actualDialSize = dialViewBoxDiameter * scale;

      // Use startTransition for non-urgent dial size updates
      // Prevents blocking animations during resize
      startTransition(() => {
        setDialSize(actualDialSize);
      });
    };

    calculateDialSize();

    // Recalculate on resize
    const resizeObserver = new ResizeObserver(calculateDialSize);
    if (containerDivRef.current) {
      resizeObserver.observe(containerDivRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    rotateToNode: async (orbitIndex: number, nodeIndex: number, targetAngle?: number): Promise<NodePosition | null> => {
      if (!svgRef.current) return null;
      const svg = d3.select(svgRef.current);
      const orbit = statusBasedOrbits[orbitIndex];
      const orbitNodes = nodes[orbitIndex] || [];
      await rotateOrbitToPosition(svg, orbitIndex, nodeIndex, orbitNodes.length, orbit, 1000, targetAngle);

      // Get the actual rendered position of the node after rotation
      const nodeElement = svg.select<SVGGElement>(`.nodes-orbit-${orbitIndex} g.node:nth-child(${nodeIndex + 1})`);
      if (nodeElement.empty()) return null;

      const nodeDOM = nodeElement.node();
      if (!nodeDOM) return null;

      // Get the bounding box of the node in viewport coordinates
      const bbox = nodeDOM.getBoundingClientRect();

      // Calculate position relative to the container (if provided) or SVG
      const referenceElement = containerRef?.current || svgRef.current;
      const referenceRect = referenceElement.getBoundingClientRect();

      // Return position relative to the reference container, centered on the node
      return {
        x: bbox.left - referenceRect.left + bbox.width / 2,
        y: bbox.top - referenceRect.top + bbox.height / 2,
      };
    },
  }), [statusBasedOrbits, nodes, containerRef]);

  // Effect 1: Initialize and update content (but NOT animation state)
  useEffect(() => {
    if (!svgRef.current) return;

    // Check if nodes or orbit config have actually changed (deep comparison)
    const currentNodesJson = JSON.stringify(nodes);
    const currentOrbitsJson = JSON.stringify(statusBasedOrbits);
    const nodesChanged = lastNodesJson.current !== currentNodesJson;
    const orbitsChanged = lastOrbitsJson.current !== currentOrbitsJson;

    // Skip recreation if already initialized and neither nodes nor orbits have changed
    if (isInitialized.current && !nodesChanged && !orbitsChanged) return;

    lastNodesJson.current = currentNodesJson;
    lastOrbitsJson.current = currentOrbitsJson;

    const svg = d3.select(svgRef.current);

    // Clear any previous content
    svg.selectAll('*').remove();

    // Create orbit rings (no click handler - only nodes should be clickable)
    createOrbitRings(svg, statusBasedOrbits, centerImagePath);

    // Create center group (dial content is rendered as React overlay, no SVG background needed)
    svg.append('g').attr('class', 'center-group');

    // Add gradient definitions
    const defs = svg.append('defs');
    const gradient = defs
      .append('linearGradient')
      .attr('id', 'centerGradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '100%')
      .attr('y2', '100%');

    gradient.append('stop').attr('offset', '0%').attr('stop-color', '#6b1b6e');
    gradient.append('stop').attr('offset', '100%').attr('stop-color', '#a21caf');

    // Add static icons on the center circle border
    const iconRadius = 115;
    const iconCount = 8;

    // Define icon SVG paths
    const iconDefs = [
      // Status icon (first/top position) - uses the status sparkle shape
      { viewBox: '0 0 27 26', paths: [
        'M6.72039 12.8585L11.5481 10.986L13.3037 5.83639C13.4867 5.29933 14.1988 5.29933 14.3819 5.83639L16.1374 10.986L20.9651 12.8585C21.4687 13.0538 21.4687 13.8134 20.9651 14.0087L16.1374 15.8812L14.3819 21.0308C14.1988 21.5679 13.4867 21.5679 13.3037 21.0308L11.5481 15.8812L6.72039 14.0087C6.2169 13.8134 6.2169 13.0538 6.72039 12.8585Z'
      ], isStatus: true, isFilled: true, fillColor: statusColor },
      // Edit/Pencil icon
      { viewBox: '0 0 24 24', paths: [
        'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7',
        'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'
      ], isEdit: true },
      // Add/Plus icon
      { viewBox: '0 0 24 24', paths: [
        'M12 5v14',
        'M5 12h14'
      ], isAdd: true },
      // Conversation icon
      { viewBox: '0 0 24 24', paths: [
        'M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z'
      ], isConversation: true },
      // Bell icon (notifications)
      { viewBox: '0 0 23 21', paths: [
        'M10 19.875H13',
        'M17.5 8.625C17.5 7.03369 16.8679 5.50758 15.7426 4.38236C14.6174 3.25715 13.0913 2.625 11.5 2.625C9.90869 2.625 8.38258 3.25715 7.25735 4.38236C6.13214 5.50758 5.5 7.03369 5.5 8.625V13.875C5.5 14.4717 5.26294 15.0441 4.84099 15.4661C4.41904 15.888 3.84673 16.125 3.25 16.125H19.75C19.1533 16.125 18.5809 15.888 18.1589 15.4661C17.737 15.0441 17.5 14.4717 17.5 13.875V8.625Z',
        'M1.75 8.43C1.75081 7.00221 2.0913 5.59511 2.74336 4.32491C3.39541 3.0547 4.34031 1.95787 5.5 1.125'
      ], isBell: true },
      // Refresh icon (replaces User icon)
      { viewBox: '0 0 24 24', paths: [
        'M23 4v6h-6',
        'M1 20v-6h6',
        'M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15'
      ], isRefresh: true },
      // Search icon
      { viewBox: '0 0 21 21', paths: [
        'M9.625 16.625C13.491 16.625 16.625 13.491 16.625 9.625C16.625 5.75901 13.491 2.625 9.625 2.625C5.75901 2.625 2.625 5.75901 2.625 9.625C2.625 13.491 5.75901 16.625 9.625 16.625Z',
        'M18.375 18.375L14.5688 14.5688'
      ]},
      // Context selector icon (filter/funnel - matches pi-filter used elsewhere)
      { viewBox: '0 0 24 24', paths: [
        'M14,20.5H10a.76.76,0,0,1-.75-.75V12L3.9,4.69a.73.73,0,0,1-.07-.78A.76.76,0,0,1,4.5,3.5h15a.76.76,0,0,1,.67.41.73.73,0,0,1-.07.78L14.75,12v7.75A.76.76,0,0,1,14,20.5ZM10.75,19h2.5V11.75a.71.71,0,0,1,.15-.44L18,5H6l4.62,6.31a.71.71,0,0,1,.15.44Z',
      ], isContext: true, isFilled: true, fillColor: '#ffffff' }
    ];

    for (let i = 0; i < iconCount; i++) {
      const angle = (i * 2 * Math.PI) / iconCount - Math.PI / 2;
      const x = CENTER_X + iconRadius * Math.cos(angle);
      const y = CENTER_Y + iconRadius * Math.sin(angle);

      // Get icon definition
      const iconDef = iconDefs[i % iconDefs.length] as { viewBox: string; paths: string[]; isRefresh?: boolean; isConversation?: boolean; isEdit?: boolean; isAdd?: boolean; isStatus?: boolean; isBell?: boolean; isContext?: boolean; isFilled?: boolean; fillColor?: string };

      // Determine icon class
      let iconClass = 'static-icon';
      if (iconDef.isRefresh) iconClass = 'static-icon refresh-icon';
      if (iconDef.isConversation) iconClass = 'static-icon conversation-icon';
      if (iconDef.isEdit) iconClass = 'static-icon edit-icon';
      if (iconDef.isAdd) iconClass = 'static-icon add-icon';
      if (iconDef.isStatus) iconClass = 'static-icon status-icon';
      if (iconDef.isBell) iconClass = 'static-icon bell-icon';
      if (iconDef.isContext) iconClass = 'static-icon context-icon';

      const iconGroup = svg.append('g')
        .attr('class', iconClass)
        .attr('transform', `translate(${x}, ${y})`)
        .style('cursor', 'pointer')
        .on('click', (event: MouseEvent) => {
          if (iconDef.isStatus) {
            // Inside a metaspace toolbar, open the toolbar's status tool for the
            // centered sphere; otherwise toggle the inline popover.
            if (onStatusToolClickRef.current) {
              onStatusToolClickRef.current();
            } else {
              onStatusIconClickRef.current(event);
            }
          } else if (iconDef.isContext) {
            onContextIconClickRef.current(event);
          } else if (iconDef.isRefresh && onRefreshRef.current) {
            onRefreshRef.current();
          } else if (iconDef.isConversation && onConversationClickRef.current) {
            onConversationClickRef.current();
          } else if (iconDef.isEdit && onEditClickRef.current) {
            onEditClickRef.current();
          } else if (iconDef.isAdd && onAddClickRef.current) {
            onAddClickRef.current();
          } else if (iconDef.isBell && onNotificationsClickRef.current) {
            onNotificationsClickRef.current();
          }
        });

      // Store reference to status icon for popover positioning
      if (iconDef.isStatus) {
        statusIconRef.current = iconGroup.node();
      }

      // Store reference to context icon for popover positioning
      if (iconDef.isContext) {
        contextIconRef.current = iconGroup.node();
      }

      // Add background circle - visible for add icon, transparent for others
      if (iconDef.isAdd) {
        // Contained background for add icon
        iconGroup.append('circle')
          .attr('r', 14)
          .attr('fill', 'transparent')
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 1.5)
          .style('pointer-events', 'all');
      } else {
        // Transparent hit area for other icons
        iconGroup.append('circle')
          .attr('r', 16)
          .attr('fill', 'transparent')
          .style('pointer-events', 'all');
      }

      // Add icon SVG - status icon needs larger size due to different viewBox
      const iconSize = (() => {
        if (iconDef.isStatus) return 28;
        if (iconDef.isAdd) return 18;
        return 24;
      })();
      const iconOffset = iconSize / 2;
      const iconSvg = iconGroup.append('svg')
        .attr('width', iconSize)
        .attr('height', iconSize)
        .attr('x', -iconOffset)
        .attr('y', -iconOffset)
        .attr('viewBox', iconDef.viewBox)
        .style('pointer-events', 'none');

      iconDef.paths.forEach(pathData => {
        const path = iconSvg.append('path').attr('d', pathData);

        if (iconDef.isFilled) {
          // Filled icon (like status) - use fill color
          path
            .attr('fill', iconDef.fillColor || '#ffffff')
            .attr('stroke', iconDef.fillColor || '#ffffff')
            .attr('stroke-width', 0.5);
        } else {
          // Stroked icon - use stroke color
          let strokeColor = '#ffffff';
          if (iconDef.isRefresh) strokeColor = '#10B6FE';
          path
            .attr('stroke', strokeColor)
            .attr('stroke-width', 1.75)
            .attr('stroke-linecap', 'round')
            .attr('stroke-linejoin', 'round')
            .attr('fill', 'none');
        }
      });
    }

    // Add navigation arrows at cardinal directions
    // Left/Right arrows cycle through dial faces, Up/Down are placeholders
    const arrowRadius = 172;
    const arrowIcons = ['▶', '▼', '◀', '▲'];
    const arrowAngles = [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2];

    arrowAngles.forEach((angle, i) => {
      const x = CENTER_X + arrowRadius * Math.cos(angle);
      const y = CENTER_Y + arrowRadius * Math.sin(angle);

      const arrowGroup = svg.append('g')
        .attr('class', 'arrow-button')
        .attr('transform', `translate(${x}, ${y})`)
        .style('cursor', 'pointer')
        .on('click', () => {
          if (i === 0) {
            // Right arrow - next dial
            goToNextDialRef.current();
          } else if (i === 2) {
            // Left arrow - previous dial
            goToPrevDialRef.current();
          }
        });

      arrowGroup.append('circle')
        .attr('r', 20)
        .attr('fill', 'none');

      arrowGroup.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '0.35em')
        .attr('fill', '#B9C1C5')
        .attr('font-size', '24px')
        .text(arrowIcons[i]);
    });

    // Create nodes for each orbit
    orbits.forEach((orbit, index) => {
      const orbitNodes = nodes[index] || [];
      if (orbitNodes.length > 0) {
        createOrbitNodes(
          svg,
          orbit,
          orbitNodes,
          index,
          (nodeId, nodeIndex, orbitIndex, label) => {
            if (onNodeClickRef.current) {
              onNodeClickRef.current({ nodeId, nodeIndex, orbitIndex, label });
            }
          },
          (nodeId, nodeIndex, orbitIndex, label) => {
            if (onNodeDoubleClickRef.current) {
              onNodeDoubleClickRef.current({ nodeId, nodeIndex, orbitIndex, label });
            }
          }
        );
      }
    });


    isInitialized.current = true;

    // Cleanup function
    return () => {
      if (svgRef.current) {
        stopAnimations(d3.select(svgRef.current));
      }
    };
  }, [nodes, statusBasedOrbits, centerImagePath, statusColor]);

  // Effect 2: Control animation start/stop separately (without recreating nodes)
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);

    if (isAnimating) {
      // Clear settled state from all orbit groups so spinning restarts fresh
      svg.selectAll('[class^="nodes-orbit-"]').attr('data-settled', null);
      animationPhaseRef.current = 'spinning';

      // Start animations from current positions for all orbits except selected ones
      statusBasedOrbits.forEach((orbit, index) => {
        if (selectedOrbits.has(index) || index === selectedOrbitIndex) {
          const selectedNodeGroups = svg.selectAll<SVGGElement, NodeData>(`.nodes-orbit-${index} g.node`);
          selectedNodeGroups.interrupt();
          return;
        }

        const orbitNodes = nodes[index] || [];
        if (orbitNodes.length > 0) {
          const nodeGroups = svg.selectAll<SVGGElement, NodeData>(`.nodes-orbit-${index} g.node`);
          if (!nodeGroups.empty()) {
            if (useVelocitySpeed && orbitNodes.some(n => n.velocity !== undefined)) {
              startOrbitAnimationFromCurrentWithVelocity(nodeGroups, orbit, orbitNodes);
            } else {
              startOrbitAnimationFromCurrent(nodeGroups, orbit);
            }
          }
        }
      });

      // Begin settlement countdown — orbits will decelerate after the spinning phase
      startSettlementCountdown();
    } else {
      // Pause: stop all animations and freeze nodes at current positions
      clearSettlementTimer();
      animationPhaseRef.current = 'settled';

      // Interrupt all running transitions to freeze nodes at current positions
      statusBasedOrbits.forEach((orbit, index) => {
        if (selectedOrbits.has(index) || index === selectedOrbitIndex) return;

        const nodeGroups = svg.selectAll<SVGGElement, NodeData>(`.nodes-orbit-${index} g.node`);
        nodeGroups.interrupt(); // Stop animations but keep current position
      });

      // Notify parent that animations have stopped
      if (onAnimationSettled) {
        onAnimationSettled();
      }
    }

    return () => {
      clearSettlementTimer();
    };
  }, [isAnimating, statusBasedOrbits, nodes, selectedOrbitIndex, selectedOrbits, useVelocitySpeed, startSettlementCountdown, clearSettlementTimer]);

  // Effect 3: Lazy load images using IntersectionObserver
  useEffect(() => {
    if (!svgRef.current) return;

    const observerOptions = {
      root: null, // Use viewport as root
      rootMargin: '50px', // Start loading slightly before entering viewport
      threshold: 0.01 // Trigger when even 1% is visible
    };

    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const imageElement = entry.target as SVGImageElement;
          const dataSrc = imageElement.dataset.src;

          if (dataSrc && !imageElement.getAttribute('href')) {
            // Load the image by setting href from data-src
            imageElement.setAttribute('href', dataSrc);
            delete imageElement.dataset.src;

            // Stop observing this image once loaded
            imageObserver.unobserve(imageElement);
          }
        }
      });
    }, observerOptions);

    // Observe all lazy-loaded images
    const lazyImages = svgRef.current.querySelectorAll('image.lazy-image');
    lazyImages.forEach(img => imageObserver.observe(img));

    // Cleanup: disconnect observer
    return () => {
      imageObserver.disconnect();
    };
  }, [nodes]); // Re-run when nodes change

  return (
    <div
      ref={containerDivRef}
      className={`orbital-visualization-molecule ${className}`}
    >
      <svg
        ref={svgRef}
        width={SVG_WIDTH}
        height={SVG_HEIGHT}
        viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
        className="orbital-svg"
      />
      {/* Dial rendered as HTML overlay, positioned at center */}
      {dialSize > 0 && (
        <div
          className="dial-overlay"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-53%, -48%)',
            width: dialSize,
            height: dialSize,
            borderRadius: '50%',
            overflow: 'hidden',
            pointerEvents: 'auto',
          }}
        >
          <DialRenderer
            dialType={DIAL_TYPES[currentDialIndex]}
            sphere={sphere}
            width={dialSize}
            height={dialSize}
            onClick={onDialClick}
          />
        </div>
      )}
      {/* Status popover - triggered by status icon in the ring */}
      <StatusPopoverMolecule
        ref={statusPopoverRef}
        sphereId={sphere?.id || ''}
        sphereName={sphere?.properties?.name || ''}
        schemaType={sphere?.properties?.schemaType}
        status={sphere?.status}
        onStatusDeployed={onStatusDeployed}
        onConfigureFactors={onConfigureFactors}
      />
      {/* Context selector popover - only render if external handler not provided (backward compatibility) */}
      {!onContextFilterClick && (
        <>
          {/* Hidden anchor for context popover — gives PrimeReact a real HTML element to position against */}
          <div ref={contextAnchorRef} style={{ position: 'fixed', pointerEvents: 'none', zIndex: -1 }} aria-hidden="true" />
          {/* Context selector popover - triggered by context icon in the ring */}
          <ContextSelectorPopoverMolecule
            ref={contextPopoverRef}
            sphereId={sphere?.id || null}
            activeContextSystemName={activeContextSystemName}
            onContextSelect={onContextSelect || (() => { /* no-op */ })}
          />
        </>
      )}
    </div>
  );
});

OrbitalVisualizationMolecule.displayName = 'OrbitalVisualizationMolecule';

// Memoize to prevent unnecessary re-renders when parent state changes
export default React.memo(OrbitalVisualizationMolecule);